import { debug } from "tone";
import Entity from "/src/js/engine/entity";
import Hitbox from "/src/js/engine/hitbox";
import Bullet from "./bullet";
import CtxTexture from "/src/js/engine/2d/ctxTexture";

import textureURL from "url:/src/img/white-guitar.png";
import textureMaskURL from "url:/src/img/white-guitar-mask.png";
import { normalizeVector } from "/src/js/engine/utils";


import damageSFXURL from "url:/src/sounds/damage.mp3";
import deathSFXURL from "url:/src/sounds/player-death.mp3";
import gsap from "gsap/gsap-core";
import Explosion from "./explosion";
import gameover from "../gameover";

export default class Player extends Entity{
    constructor({engine, inputs, music}){
        super({engine,type:"player"});
        this.music = music;
        this.position = {x: 0, y:this.engine.height/2};
        this.inputs = inputs;
        this.controllerPosition = {x: 0, y: 0};

        this.hitbox = new Hitbox({...this.position, w: 10, h: 10});
        this.attackSpeed = 0.1;
        this.attackTimer = 0;
        this.maxHp = 7;
        this.hp = this.maxHp;
        this.hpBarsDOM = document.getElementById("player-hp").querySelectorAll(".healthbar__inner div");

        this.score = 0;
        this.hitstreak = 0;
        this.scoreMult = 1;
        this.scoreDOM = document.getElementById("score");
        this.scoreMultDOM = document.getElementById("score-mult");
        this.scoreTime = 0;

        this.smoothing = 0.3;

        this.texture = new CtxTexture(textureURL);
        this.textureMask = new CtxTexture(textureMaskURL);
        this.rotation = 0;
        this.hitted = false;
        this.regenTimer = 0;
        this.immortalityTimer = 0;
    }

    update(){
        if(this.gameover) return;
        const s =  this.engine.scaleFactor;
        this.scoreTime += this.engine.time.delta;

        //mouse control
        if(this.inputs.hasMouseUpdate()) this.controllerPosition = {...this.inputs.mousepos};

        //keyboard control
        const speed = 800 * s * this.engine.time.delta;
        if(this.inputs.getKey("ArrowRight")) this.controllerPosition.x += speed;
        if(this.inputs.getKey("ArrowLeft")) this.controllerPosition.x -= speed;
        if(this.inputs.getKey("ArrowUp")) this.controllerPosition.y -= speed;
        if(this.inputs.getKey("ArrowDown")) this.controllerPosition.y += speed;

        let direction = {
            x: (this.controllerPosition.x/window.innerWidth) * this.engine.width - this.position.x ,
            y: (this.controllerPosition.y/window.innerHeight) * this.engine.height - this.position.y 
        };

        this.position.y += direction.y * this.smoothing;
        this.position.x += direction.x * this.smoothing;
        this.rotation = Math.atan2(direction.y, direction.x + 300 * s);
        this.immortalityTimer -= this.engine.time.delta;

        this.attackTimer -= this.engine.time.delta;
        this.regenTimer -= this.engine.time.delta;
        if(this.inputs.getKey("click") || this.inputs.getKey("Space")){
            if(this.attackTimer <= 0){
                this.attackTimer = this.attackSpeed;
                let direction = normalizeVector( {
                    x: 1, 
                    y: (Math.random() - 0.5) * 2 * 0.025
                });
                new Bullet({engine:this.engine, position:this.position, direction, player: this});
            }
        }else if(this.regenTimer <= 0){
            this.hp += this.engine.time.delta;
            if(this.hp > this.maxHp) this.hp = this.maxHp;
        }
    
        this.collision();
        this.updateUI();
    }

    updateUI(){
        const percent = ((1-this.hp/this.maxHp) * 100);
        this.hpBarsDOM[0].style="transform: translateX("+ percent +"%);";
        this.hpBarsDOM[1].style="transform: translateX("+ percent +"%);";
        this.scoreDOM.textContent=this.score;
        this.scoreMultDOM.textContent=this.scoreMult;
    }

    collision(){
        this.engine.entities.forEach(e => {
            if((e.type == "note" || e.type == "rocket" ) && e.hitbox.overlaps(this.hitbox)){
                e.hit();
                this.regenTimer = 1;
        
                if(this.immortalityTimer <= 0){
                    this.immortalityTimer = 0.3;
                    this.hp -= e.type == "note" ? 1 : 2;
                    this.hitstreak /= 2;
                    if(this.hitstreak<0)this.hitstreak=0;
                    this.calcScoreMult();
                    if(this.hp <= 0 ){
                        this.death();
                    }
                }

                if(this.hp > 0){
                    this.damageFx(e);
                }
            }
        });
    }

    death(){
        this.music.stop();
        gsap.timeline()
            .to(".hud",{duration: .5, autoAlpha: 0, ease:"power3.inOut" },"0")
            .fromTo(this.engine.renderer.passes[1].material.uniforms.damage,{value: 10 }, { duration: 1, value: 0.05 },"0")
            .fromTo(this.engine.renderer.passes[1].material.uniforms.deathtransition,{value: 0 }, { duration: 0.5, value: 1 },"0.2");
        gameover({win: false, score: this.score, time: this.scoreTime});
        this.engine.entities.forEach(e =>{
            this.engine.destroyEntity(e);
        });
        new Explosion({engine: this.engine, position: this.position, size: 10000});
        const sfx = new Audio(deathSFXURL);
        sfx.volume = 1;
        sfx.play();

    }

    damageFx(e){
        this.engine.renderer.passes[1].material.uniforms.damage.value = 1;
        clearTimeout(this.damageTimeout);
        this.damageTimeout = setTimeout(() => this.engine.renderer.passes[1].material.uniforms.damage.value = 0, e.type == "note" ? 300 : 1000);
        this.hitted = true;
        clearTimeout(this.hitTimeout);
        this.hitTimeout = setTimeout(() => this.hitted = false, 300);
        const sfx = new Audio(damageSFXURL);
        sfx.volume =  e.type == "note" ? 0.2 : 0.3;
        sfx.play();
    }

    render(ctx){
        if(!this.texture.ready) return;
        const s =  this.engine.scaleFactor;
        const texSize = 100 * s;
        const glowRadius = 70 * s;

        ctx.save();
        ctx.translate(this.position.x, this.position.y - 3);
        ctx.rotate(this.rotation);

        //light
        const gradient = ctx.createRadialGradient(0,0,0, 0,0,glowRadius);
        gradient.addColorStop(0, 'rgba(39, 58, 155, 0.5)');
        gradient.addColorStop(1, 'rgba(39, 58, 155, 0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(-glowRadius, -glowRadius, glowRadius * 2, glowRadius * 2);

        //texture
        this.texture.draw(ctx, texSize);
        if(this.hitted) this.textureMask.draw(ctx, texSize);
        ctx.restore();
        
        //hitbox update
        this.hitbox.set({...this.position, w: 10 * s, h: 10 * s});
    }

    calcScoreMult(){
        let current = parseInt(this.hitstreak/3) + 1;
        if(current != this.scoreMult){
            gsap.timeline()
                .to(".score-mult-wrapper",{duration:.1, scale: 0.9})
                .to(".score-mult-wrapper",{duration:.2, scale: 1});
            this.scoreMult = current;
        }
    }

    addScore(){
        this.hitstreak++;
        this.calcScoreMult();
        this.score += this.scoreMult;
    }
}
